import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, Plus, ArrowLeft, Search, Upload, X, Check, ChevronRight, UserPlus, Link2, Calendar, CreditCard, FileText, Filter, Edit2, FileUp, Download } from "lucide-react";
import { useAuth, API } from "../../context/AuthContext";
import { Modal } from "../../components/shared/Modal";
import { WalletPanel } from "./WalletPanel";
import { PersonActivityCard } from "./PersonActivityCard";
import { MembershipValueCard } from "./MembershipValueCard";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";

export default function ManageMembers() {
  const [members, setMembers] = useState([]);
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [showLinkFamily, setShowLinkFamily] = useState(false);
  const [activityKey, setActivityKey] = useState(0);
  const [familySearch, setFamilySearch] = useState("");
  const [familyResults, setFamilyResults] = useState([]);
  const [showMarkFeePaid, setShowMarkFeePaid] = useState(false);
  const [feePaymentDate, setFeePaymentDate] = useState("");
  const [sepaUploadTarget, setSepaUploadTarget] = useState(null); // {memberId?, familyId?} + pending File
  const [sepaMandateDate, setSepaMandateDate] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [memberTypes, setMemberTypes] = useState([]);
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterType, setFilterType] = useState("");
  const [filterDuration, setFilterDuration] = useState("");
  const [filterMode, setFilterMode] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", dob: "", address: "", mobile_number: "", member_type_id: "" });
  const [deactivatePreview, setDeactivatePreview] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => { checkAgeTransitions(); fetchMembers(); fetchTypes(); }, []);

  const checkAgeTransitions = async () => {
    try { await authAxios.post("/members/check-age-transitions"); } catch (err) { console.error("Request failed:", err?.message || err); }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try { const res = await authAxios.get("/members/"); setMembers(res.data); }
    catch { toast.error("Failed to load members"); }
    finally { setLoading(false); }
  };

  const fetchTypes = async () => {
    try { const res = await authAxios.get("/members/types"); setTypes(res.data); setMemberTypes(res.data); }
    catch (err) { console.error("Request failed:", err?.message || err); }
  };

  const handleAdd = async () => {
    if (!form.name || !form.email || !form.dob || !form.address) return toast.error("Fill all required fields");
    if (!form.member_type_id) return toast.error("Select member type");
    try {
      await authAxios.post("/members/", { ...form, satzung_consent: true, auto_subscription_consent: true, gdpr_consent: true });
      toast.success("Member created");
      setShowAdd(false); setForm({ name: "", email: "", dob: "", address: "", mobile_number: "", member_type_id: "" });
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDeactivate = async (memberId, isActive) => {
    // iter128: replaced the old confirm-less deactivate with a two-step
    // flow — fetch the preview so the AlertDialog can show the exact
    // Stripe subscription id + annual fee + child-cascade that will be
    // affected, then require explicit FA confirmation. Prevents accidental
    // clicks from silently wiping a paying member's subscription.
    if (!isActive) {
      try { await authAxios.put(`/members/${memberId}/activate`); toast.success("Member activated"); }
      catch (e) { toast.error(e.response?.data?.detail || "Error"); }
      fetchMembers(); setSelectedMember(null); return;
    }
    try {
      const res = await authAxios.get(`/members/${memberId}/deactivation-preview`);
      setDeactivatePreview(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load preview"); }
  };

  const confirmDeactivate = async () => {
    if (!deactivatePreview) return;
    const memberId = deactivatePreview.member_id;
    setDeactivating(true);
    try {
      const res = await authAxios.put(`/members/${memberId}/deactivate`);
      const cascaded = (res.data && res.data.cascaded_deactivations) || [];
      if (cascaded.length > 0) {
        const names = cascaded.map(c => c.name || c.member_id).join(", ");
        toast.warning(
          `Member deactivated. Also auto-deactivated ${cascaded.length} child member(s) because no other active adult remained: ${names}`,
          { duration: 12000 }
        );
      } else {
        toast.success("Member deactivated");
      }
      setDeactivatePreview(null);
      fetchMembers(); setSelectedMember(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setDeactivating(false); }
  };

  const handleSearchFamily = async () => {
    if (!familySearch.trim()) return;
    try {
      const res = await authAxios.get(`/members/search/${familySearch}`);
      // iter132: only surface ACTIVE + APPROVED members / guests in the
      // family-link picker. Deactivated / pending / incomplete profiles
      // must never be pickable — the FA saw stale rows because the
      // search endpoint returns the full lookup surface.
      const filtered = res.data.filter(m =>
        m.member_id !== selectedMember?.member_id &&
        m.is_active !== false &&
        (m.approval_status === "approved" || m.type === "guest"),
      );
      setFamilyResults(filtered);
    }
    catch { setFamilyResults([]); }
  };

  const handleLinkFamily = async (targetMemberId) => {
    try {
      await authAxios.put(`/members/${selectedMember.member_id}/link-family/${targetMemberId}`);
      toast.success("Family linked successfully");
      setShowLinkFamily(false); setFamilySearch(""); setFamilyResults([]);
      const res = await authAxios.get(`/members/${selectedMember.member_id}`);
      setSelectedMember(res.data);
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleMarkFeePaid = async () => {
    if (!feePaymentDate) return toast.error("Please select a payment date");
    try {
      await authAxios.post(`/members/${selectedMember.member_id}/mark-fee-paid`, { payment_date: feePaymentDate });
      toast.success("Fee marked as paid");
      setShowMarkFeePaid(false); setFeePaymentDate("");
      const res = await authAxios.get(`/members/${selectedMember.member_id}`);
      setSelectedMember(res.data);
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const startEdit = () => {
    setEditForm({
      member_type_id: selectedMember.member_type_id || "",
      membership_start_date: selectedMember.membership_start_date || "",
      mobile_number: selectedMember.mobile_number || "",
      address: selectedMember.address || "",
    });
    setEditMode(true);
  };

  const handleInlineSave = async (field, value) => {
    try {
      const res = await authAxios.put(`/members/${selectedMember.member_id}`, { [field]: value });
      toast.success("Updated");
      setSelectedMember(res.data);
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };


  const [bulkPreview, setBulkPreview] = useState(null);  // { rows, filename, total, valid_count, error_count }
  const [bulkSelected, setBulkSelected] = useState({}); // row_num -> bool
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await authAxios.get("/members-bulk/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "maitree_members_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const handleUpload = async (file) => {
    // iter132: two-stage flow. Preview first — never write on upload.
    if (!file) return;
    setBulkBusy(true);
    setBulkResult(null);
    setBulkPreview(null);
    setBulkSelected({});
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await authAxios.post("/members-bulk/preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkPreview(res.data);
      // Default: every valid (error-less) row is pre-selected. FA can uncheck.
      const initial = {};
      (res.data.rows || []).forEach(r => { initial[r.row_num] = !r.error; });
      setBulkSelected(initial);
      if (res.data.valid_count === 0 && res.data.error_count > 0) {
        toast.error(`All ${res.data.error_count} row(s) have errors — fix your Excel and re-upload.`);
      } else {
        toast.success(`${res.data.valid_count} valid, ${res.data.error_count} row(s) need attention`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBulkBusy(false); }
  };

  const confirmBulkCommit = async () => {
    if (!bulkPreview) return;
    const rowsToSend = (bulkPreview.rows || []).filter(r => !r.error && bulkSelected[r.row_num]);
    if (!rowsToSend.length) { toast.error("Select at least one valid row to import"); return; }
    setBulkBusy(true);
    try {
      const res = await authAxios.post("/members-bulk/commit", { filename: bulkPreview.filename, rows: rowsToSend });
      setBulkResult(res.data);
      setBulkPreview(null);
      if (res.data.created_count > 0) toast.success(`${res.data.created_count} member(s) activated`);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} row(s) still had issues`);
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBulkBusy(false); }
  };

  const cancelBulkPreview = () => { setBulkPreview(null); setBulkSelected({}); };

  const openSepaUploadDialog = (target, file) => {
    // target: { memberId, currentFeeDate? } or { familyId, currentFeeDate? }.
    // Pre-populates with the existing last_fee_paid_date (from Excel import).
    // FA can leave it untouched (no cadence reset) or edit it to refresh anchor.
    if (!file) return;
    setSepaMandateDate(target.currentFeeDate || "");
    setSepaUploadTarget({ ...target, file });
  };

  const confirmSepaUpload = async () => {
    if (!sepaUploadTarget) return;
    const { memberId, familyId, file } = sepaUploadTarget;
    const formData = new FormData();
    formData.append("file", file);
    // Only send the date if the FA actually populated it (they may leave it blank).
    if (sepaMandateDate) formData.append("last_membership_fee_paid_date", sepaMandateDate);
    try {
      if (memberId) {
        const res = await authAxios.post(`/members-bulk/${memberId}/sepa`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(res.data.activated ? "SEPA uploaded — member activated + welcome email sent" : "SEPA uploaded");
        fetchMembers();
        if (selectedMember?.member_id === memberId) {
          const r = await authAxios.get(`/members/${memberId}`);
          setSelectedMember(r.data);
        }
      } else if (familyId) {
        const res = await authAxios.post(`/members-bulk/family/${familyId}/sepa`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${res.data.activated_count || 0} family member(s) activated`);
        fetchMembers();
      }
      setSepaUploadTarget(null); setSepaMandateDate("");
    } catch (e) { toast.error(e.response?.data?.detail || "SEPA upload failed"); }
  };

  const downloadSepaMandate = async (memberId) => {
    try {
      const res = await authAxios.get(`/members-bulk/${memberId}/sepa`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("No SEPA mandate on file"); }
  };

  // Subscription bucketing:
  //   - Active Subscriptions: is_active + approved AND (subscription_mode ∈ {stripe, sepa} OR fee-exempt free members).
  //   - Subscription Due: is_active + approved AND subscription_mode == "na" AND annual_fee > 0.
  // Free members (Child / Vorstand / Honorary — annual_fee == 0) always live under Active Subscriptions
  // per user spec: no automated fee to chase = no due state.
  const isFreeMember = (m) => (m.annual_fee ?? 0) <= 0;
  const isActiveSubscription = (m) =>
    m.is_active && m.approval_status === "approved" &&
    (m.subscription_mode === "stripe" || m.subscription_mode === "sepa" || isFreeMember(m));
  const isSubscriptionDue = (m) =>
    m.is_active && m.approval_status === "approved" &&
    m.subscription_mode === "na" && !isFreeMember(m);

  const filtered = members.filter(m => {
    const q = search.toLowerCase();
    const matchesSearch = !q || m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) || m.member_id?.toLowerCase().includes(q);
    // Status filter
    let matchesStatus = true;
    if (filterStatus === "active") matchesStatus = isActiveSubscription(m);
    else if (filterStatus === "subscription_due") matchesStatus = isSubscriptionDue(m);
    else if (filterStatus === "pending") matchesStatus = m.approval_status === "pending";
    else if (filterStatus === "cancelled") matchesStatus = !m.is_active && m.approval_status === "approved";
    else if (filterStatus === "incomplete") matchesStatus = m.approval_status === "incomplete";
    // "all" matches everything
    // Type filter
    const matchesType = !filterType || m.member_type_name === filterType;
    // Payment mode filter — "stripe" / "sepa" / "na" (fee-exempt members
    // are treated as "na" for the purpose of this filter so an FA looking
    // for "no automated subscription" catches both bulk-imported members
    // and children).
    let matchesMode = true;
    if (filterMode) {
      const rowMode = isFreeMember(m) ? "na" : (m.subscription_mode || "na");
      matchesMode = rowMode === filterMode;
    }
    // Duration filter
    let matchesDuration = true;
    if (filterDuration && m.membership_start_date) {
      const years = Math.floor((new Date() - new Date(m.membership_start_date)) / (365.25 * 24 * 60 * 60 * 1000));
      matchesDuration = years >= parseInt(filterDuration);
    } else if (filterDuration) {
      matchesDuration = false;
    }
    return matchesSearch && matchesStatus && matchesType && matchesMode && matchesDuration;
  });

  const activeCount = members.filter(isActiveSubscription).length;
  const subscriptionDueCount = members.filter(isSubscriptionDue).length;
  const pendingCount = members.filter(m => m.approval_status === "pending").length;
  const cancelledCount = members.filter(m => !m.is_active && m.approval_status === "approved").length;
  const incompleteCount = members.filter(m => m.approval_status === "incomplete").length;

  // SEPA upload modal — same shared-across-branches pattern as the
  // deactivate dialog. Both the detail view AND the Subscription-Due /
  // family list rows fire `openSepaUploadDialog`, so the modal MUST live
  // in both return trees. Otherwise the state flips but the modal only
  // mounts once the FA hits Back (or drills into the profile).
  const sepaUploadModal = sepaUploadTarget && (
    <Modal title="Upload signed SEPA mandate" onClose={() => { setSepaUploadTarget(null); setSepaMandateDate(""); }}>
      <div className="space-y-4">
        <p className="text-slate-600 text-sm">
          {sepaUploadTarget.familyId ? "Attach the family SEPA mandate PDF." : "Attach the signed SEPA mandate PDF."}
        </p>
        <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600">
          File: <strong className="text-[#1e3a5f]">{sepaUploadTarget.file?.name}</strong>
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Last fee-paid date <span className="text-slate-400">(optional)</span></label>
          <input type="date"
            max={new Date().toISOString().slice(0, 10)}
            value={sepaMandateDate}
            onChange={e => setSepaMandateDate(e.target.value)}
            className="w-full p-3 rounded-xl border"
            data-testid="sepa-mandate-date" />
          <p className="text-[11px] text-slate-400 mt-1">
            Leave untouched to keep the current anchor. Change ONLY if the mandate signing date is different from the imported fee-paid date — that resets the renewal reminder cadence.
          </p>
        </div>
        <button onClick={confirmSepaUpload}
          className="w-full py-3 rounded-full bg-orange-500 text-white font-bold"
          data-testid="confirm-sepa-upload">Upload &amp; activate</button>
      </div>
    </Modal>
  );

  // Deactivate confirmation dialog — declared here so it renders in BOTH
  // the member-detail early-return AND the main list view. Without this,
  // clicking Deactivate on a member profile would only surface the dialog
  // AFTER the FA hit Back (because the AlertDialog only lived in the outer
  // return tree, and the early-return short-circuited it).
  const deactivateDialog = (
    <AlertDialog open={!!deactivatePreview} onOpenChange={(v) => !v && setDeactivatePreview(null)}>
      <AlertDialogContent data-testid="deactivate-preview-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate {deactivatePreview?.member_name || "member"}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              {deactivatePreview?.has_active_subscription ? (
                <p data-testid="deactivate-sub-line">
                  Will cancel Stripe subscription{" "}
                  <span className="font-mono text-xs bg-slate-100 px-1 rounded">{deactivatePreview.stripe_subscription_id}</span>{" "}
                  billing <span className="font-semibold">€{(deactivatePreview.annual_fee || 0).toFixed(2)}/yr</span>.
                </p>
              ) : (
                <p>No active Stripe subscription is tagged to this member.</p>
              )}
              {deactivatePreview?.wallet_balance > 0 && (
                <p className="text-red-600" data-testid="deactivate-balance-blocker">
                  Blocked: member still has €{deactivatePreview.wallet_balance.toFixed(2)} unused wallet balance. Refund it first.
                </p>
              )}
              {deactivatePreview?.cascaded_children?.length > 0 && (
                <p className="text-amber-700" data-testid="deactivate-cascade-line">
                  Also auto-deactivates {deactivatePreview.cascaded_children.length} child member(s) because no other active adult remains:{" "}
                  {deactivatePreview.cascaded_children.map(c => c.name || c.member_id).join(", ")}.
                </p>
              )}
              <p className="text-slate-500 text-xs">This cannot be undone. The member would need to re-register.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="deactivate-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="deactivate-confirm-btn"
            disabled={deactivating || (deactivatePreview?.wallet_balance || 0) > 0}
            onClick={confirmDeactivate}
            className="bg-red-600 hover:bg-red-700"
          >
            {deactivating ? "Deactivating…" : "Confirm deactivate"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  const uniqueTypes = [...new Set(members.map(m => m.member_type_name).filter(Boolean))];

  if (selectedMember) {
    return (
      <div data-testid="member-detail">
        <button onClick={() => setSelectedMember(null)} className="flex items-center gap-2 text-slate-600 mb-6"><ArrowLeft className="w-5 h-5" /> Back to Members</button>
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm mb-6">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-[#8b5cf6] flex items-center justify-center shrink-0"><Users className="w-7 h-7 text-white" /></div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-[#1e3a5f] break-words">{selectedMember.name}</h2>
              <p className="text-slate-500 text-sm break-all">{selectedMember.email}</p>
              <p className="text-slate-400 font-mono text-xs">{selectedMember.member_id}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
              {/* iter132: payment-mode badge lifted here to sit next to the
                  Deactivate CTA. Icon-first so the FA can tell subscription
                  mode at a glance without reading the tile grid. */}
              {(() => {
                const mode = selectedMember.subscription_mode;
                const isFree = (selectedMember.annual_fee || 0) <= 0;
                const badge =
                  mode === "stripe"
                    ? { icon: <CreditCard className="w-4 h-4" />, label: "Stripe", cls: "bg-blue-50 text-blue-700 border-blue-200" }
                    : mode === "sepa"
                      ? { icon: <FileText className="w-4 h-4" />, label: "SEPA", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
                      : isFree
                        ? { icon: <Check className="w-4 h-4" />, label: "Free", cls: "bg-slate-100 text-slate-600 border-slate-200" }
                        : { icon: <Calendar className="w-4 h-4" />, label: "Due", cls: "bg-orange-50 text-orange-700 border-orange-200" };
                return (
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.cls}`}
                    title={mode === "stripe" ? "Stripe subscription" : mode === "sepa" ? "SEPA mandate" : isFree ? "Free member (no fee)" : "Subscription due"}
                    data-testid="payment-mode-badge"
                  >
                    {badge.icon}
                    <span>{badge.label}</span>
                  </span>
                );
              })()}
              {selectedMember.is_active ? (
                <button onClick={() => handleDeactivate(selectedMember.member_id, true)} className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-600" data-testid="toggle-active-btn">Deactivate</button>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-500">Inactive</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-slate-500 text-xs">Type</p>
              {editMode ? (
                <select value={editForm.member_type_id} onChange={e => { setEditForm({...editForm, member_type_id: e.target.value}); handleInlineSave("member_type_id", e.target.value); }} className="w-full mt-1 px-2 py-1 rounded-lg border text-sm font-bold text-[#1e3a5f]">
                  {memberTypes.filter(t => t.name !== "Child").map(t => <option key={t.id} value={t.id}>{t.name} ({"\u20ac"}{t.annual_fee}/yr)</option>)}
                </select>
              ) : <p className="font-bold text-[#1e3a5f]">{selectedMember.member_type_name || "—"}</p>}
            </div>
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-slate-500 text-xs">Annual Fee</p><p className="font-bold text-green-600">{"\u20ac"}{(selectedMember.annual_fee || 0).toFixed(2)}</p></div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-slate-500 text-xs">Start Date</p>
              {editMode ? (
                <input type="date" value={editForm.membership_start_date} onChange={e => { setEditForm({...editForm, membership_start_date: e.target.value}); handleInlineSave("membership_start_date", e.target.value); }} className="w-full mt-1 px-2 py-1 rounded-lg border text-sm font-bold" />
              ) : <p className="font-bold">{selectedMember.membership_start_date || "—"}</p>}
            </div>
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-slate-500 text-xs">End Date</p><p className="font-bold">{selectedMember.membership_end_date || "Active"}</p></div>
            <div className={`rounded-xl p-3 ${selectedMember.last_membership_fee_paid_date ? "bg-green-50" : "bg-amber-50"}`}>
              <p className="text-slate-500 text-xs">Last Fee Paid</p>
              <p className={`font-bold ${selectedMember.last_membership_fee_paid_date ? "text-green-600" : "text-amber-600"}`}>
                {selectedMember.last_membership_fee_paid_date || "Not paid"}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-slate-500 text-xs">Status</p>
              <p className={`font-bold ${selectedMember.approval_status === "approved" ? "text-green-600" : selectedMember.approval_status === "pending" ? "text-amber-600" : "text-red-500"}`}>{selectedMember.approval_status?.toUpperCase()}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-slate-500 text-xs">Mobile</p>
              {editMode ? (
                <input value={editForm.mobile_number} onChange={e => setEditForm({...editForm, mobile_number: e.target.value})} onBlur={() => handleInlineSave("mobile_number", editForm.mobile_number)} className="w-full mt-1 px-2 py-1 rounded-lg border text-sm font-bold" />
              ) : <p className="font-bold">{selectedMember.mobile_number || "—"}</p>}
            </div>
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-slate-500 text-xs">DOB</p><p className="font-bold">{selectedMember.dob || "—"}</p></div>
            <div className="bg-slate-50 rounded-xl p-3 col-span-2">
              <p className="text-slate-500 text-xs">Address</p>
              {editMode ? (
                <textarea value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} onBlur={() => handleInlineSave("address", editForm.address)} rows={2} className="w-full mt-1 px-2 py-1 rounded-lg border text-sm font-bold resize-none" />
              ) : <p className="font-bold">{selectedMember.address || "—"}</p>}
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            {selectedMember.is_active && (
              <button onClick={() => {
                if (!editMode) {
                  setEditForm({
                    member_type_id: selectedMember.member_type_id || "",
                    membership_start_date: selectedMember.membership_start_date || "",
                    mobile_number: selectedMember.mobile_number || "",
                    address: selectedMember.address || "",
                  });
                }
                setEditMode(!editMode);
              }} className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 border ${editMode ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-blue-50 text-blue-700 border-blue-200"}`} data-testid="edit-member-btn">
                <Edit2 className="w-5 h-5" /> {editMode ? "Done Editing" : "Edit"}
              </button>
            )}
            <button onClick={() => { setShowMarkFeePaid(true); setFeePaymentDate(new Date().toISOString().split("T")[0]); }} className="flex-1 py-3 rounded-xl bg-green-50 text-green-700 font-bold flex items-center justify-center gap-2 border border-green-200" data-testid="mark-fee-paid-btn"><CreditCard className="w-5 h-5" /> Mark Fee Paid</button>
            <button onClick={() => setShowLinkFamily(true)} className="flex-1 py-3 rounded-xl bg-purple-50 text-[#8b5cf6] font-bold flex items-center justify-center gap-2 border border-purple-200" data-testid="link-family-btn"><Link2 className="w-5 h-5" /> Link Family</button>
          </div>

          {selectedMember.payment_method === "sepa" && (
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/wallet/sepa-mandate/${selectedMember.member_id}/pdf`, { credentials: "include" });
                  if (!res.ok) { toast.error("No SEPA mandate found"); return; }
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank");
                } catch { toast.error("Failed to download"); }
              }}
              className="w-full py-2 mb-4 rounded-xl bg-blue-50 text-blue-700 font-medium flex items-center justify-center gap-2 border border-blue-200 text-sm"
              data-testid="download-sepa-btn"
            ><FileText className="w-4 h-4" /> Download SEPA Mandate PDF</button>
          )}

          {selectedMember.sepa_mandate_url && (
            <button
              onClick={() => downloadSepaMandate(selectedMember.member_id)}
              className="w-full py-2 mb-4 rounded-xl bg-orange-50 text-orange-700 font-medium flex items-center justify-center gap-2 border border-orange-200 text-sm"
              data-testid="download-sepa-bulk-btn"
            ><FileText className="w-4 h-4" /> Download Signed SEPA Mandate {selectedMember.sepa_uploaded_at ? `(uploaded ${selectedMember.sepa_uploaded_at.slice(0,10)})` : ""}</button>
          )}

          {selectedMember.approval_status === "sepa_pending" && (
            <div className="w-full mb-4 rounded-xl bg-orange-50 border border-orange-200 p-3 flex items-center gap-3">
              <FileUp className="w-5 h-5 text-orange-600" />
              <div className="flex-1">
                <p className="text-orange-800 text-sm font-bold">Awaiting signed SEPA mandate</p>
                <p className="text-orange-600 text-xs">Upload the PDF to activate this member (wallet, login &amp; welcome email are generated automatically).</p>
              </div>
              <label className="cursor-pointer px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-bold whitespace-nowrap" data-testid="upload-sepa-detail">
                Upload SEPA
                <input type="file" accept="application/pdf" className="hidden"
                  onChange={e => { if (e.target.files?.[0]) openSepaUploadDialog({ memberId: selectedMember.member_id, currentFeeDate: selectedMember.last_membership_fee_paid_date }, e.target.files[0]); e.target.value = ""; }}
                />
              </label>
            </div>
          )}

          {/* SEPA-mode alert: pending members who intend to pay via SEPA — remind FA to check offline mandate BEFORE approving. */}
          {selectedMember.approval_status === "pending" && selectedMember.payment_method === "sepa" && (
            <div className="w-full mb-4 rounded-xl bg-red-50 border border-red-300 p-3 flex items-start gap-3" data-testid="sepa-offline-alert">
              <FileText className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 text-sm font-bold">SEPA payment mode — verify offline mandate first</p>
                <p className="text-red-700 text-xs mt-1">
                  This applicant chose SEPA Direct Debit. Before approving, make sure the signed paper mandate is on file at the Maitree e.V. office. After approval you can attach the scanned PDF to this profile via &quot;Upload SEPA&quot; above.
                </p>
              </div>
            </div>
          )}

          {/* Subscription-mode + last-fee-paid guard hint on approved members. */}
          {selectedMember.is_active && selectedMember.approval_status === "approved" && selectedMember.subscription_mode === "stripe" && (
            <div className="w-full mb-4 rounded-xl bg-blue-50 border border-blue-200 p-2.5 text-xs text-blue-700" data-testid="stripe-mode-note">
              <strong>Stripe subscription active.</strong> Last fee-paid date is managed automatically by Stripe and cannot be edited.
            </div>
          )}

          {selectedMember.family_members && selectedMember.family_members.length > 0 && (
            <div className="mt-4">
              <h3 className="font-bold text-sm mb-2 text-slate-600">Family Members</h3>
              <div className="space-y-2">
                {selectedMember.family_members.map(fm => (
                  <div key={fm.member_id} className="bg-purple-50 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#8b5cf6] flex items-center justify-center"><span className="text-white text-sm font-bold">{fm.name?.charAt(0)}</span></div>
                    <div><p className="font-medium text-sm">{fm.name}</p><p className="text-slate-400 text-xs">{fm.member_id}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4">
            <WalletPanel customerId={selectedMember.member_id} active={selectedMember.is_active !== false} onChanged={() => setActivityKey(k => k + 1)} />
            <MembershipValueCard memberId={selectedMember.member_id} />
            <PersonActivityCard kind="member" id={selectedMember.member_id} refreshKey={activityKey} />
          </div>
        </div>

        {showLinkFamily && <Modal title="Link Family Member" onClose={() => { setShowLinkFamily(false); setFamilySearch(""); setFamilyResults([]); }}>
          <div className="flex gap-2 mb-4">
            <input value={familySearch} onChange={e => setFamilySearch(e.target.value)} placeholder="Search by name, email, or ID..." className="flex-1 p-3 rounded-xl border" data-testid="family-search-input" />
            <button onClick={handleSearchFamily} className="px-4 py-3 rounded-xl bg-[#8b5cf6] text-white"><Search className="w-5 h-5" /></button>
          </div>
          {familyResults.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {familyResults.map(r => (
                <button key={r.member_id} onClick={() => handleLinkFamily(r.member_id)} className="w-full bg-slate-50 rounded-xl p-3 flex items-center justify-between hover:bg-slate-100" data-testid={`family-result-${r.member_id}`}>
                  <div className="text-left"><p className="font-medium">{r.name}</p><p className="text-slate-400 text-xs">{r.email} | {r.member_id}</p></div>
                  <Link2 className="w-4 h-4 text-[#8b5cf6]" />
                </button>
              ))}
            </div>
          )}
          {familySearch && familyResults.length === 0 && <p className="text-center text-slate-400 py-4">No results found</p>}
        </Modal>}

        {showMarkFeePaid && <Modal title="Mark Fee as Paid" onClose={() => { setShowMarkFeePaid(false); setFeePaymentDate(""); }}>
          <div className="space-y-4">
            <p className="text-slate-600 text-sm">Record a manual fee payment for <strong>{selectedMember.name}</strong>.</p>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Payment Date</label>
              <input type="date" value={feePaymentDate} onChange={e => setFeePaymentDate(e.target.value)} className="w-full p-3 rounded-xl border" data-testid="fee-payment-date" />
            </div>
            {selectedMember.last_membership_fee_paid_date && (
              <p className="text-xs text-slate-400">Previous fee paid: {selectedMember.last_membership_fee_paid_date}</p>
            )}
            <button onClick={handleMarkFeePaid} className="w-full py-3 rounded-full bg-green-600 text-white font-bold" data-testid="confirm-mark-fee-paid">Confirm Payment</button>
          </div>
        </Modal>}

        {sepaUploadModal}
        {deactivateDialog}
      </div>
    );
  }

  return (
    <div data-testid="manage-members">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-[#1e3a5f]">Members</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowUpload(true)} className="w-10 h-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center" data-testid="upload-btn" title="Upload Excel"><Upload className="w-5 h-5" /></button>
          <button                           onClick={() => { toast.info("Adding new members is temporarily disabled — new applications must come through the public /member-register flow."); }}
                          disabled
                          className="w-10 h-10 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center cursor-not-allowed opacity-60"
                          title="Adding new members is temporarily disabled"
                          data-testid="add-member-btn"
                        ><Plus className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or member ID..." className="w-full p-3 rounded-xl border" data-testid="member-search" />
      </div>

      {/* Status filter chips */}
      <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
        {[
          { id: "active", label: `Active Subscriptions (${activeCount})`, color: "green" },
          { id: "subscription_due", label: `Subscription Due (${subscriptionDueCount})`, color: "orange" },
          { id: "pending", label: `Pending (${pendingCount})`, color: "amber" },
          { id: "cancelled", label: `Cancelled (${cancelledCount})`, color: "red" },
          { id: "incomplete", label: `Incomplete (${incompleteCount})`, color: "slate" },
          { id: "all", label: `All (${members.length})`, color: "purple" },
        ].map(f => (
          <button key={f.id} onClick={() => setFilterStatus(f.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${filterStatus === f.id ? `bg-${f.color}-500 text-white` : `bg-${f.color}-50 text-${f.color}-600`}`} data-testid={`filter-${f.id}`} style={filterStatus === f.id ? { backgroundColor: f.color === "green" ? "#22c55e" : f.color === "amber" ? "#f59e0b" : f.color === "orange" ? "#f97316" : f.color === "red" ? "#ef4444" : f.color === "purple" ? "#8b5cf6" : "#64748b", color: "white" } : {}}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Advanced filters */}
      <div className="flex gap-1.5 mb-4">
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="flex-1 min-w-0 px-2 sm:px-3 py-2 rounded-xl border text-xs bg-white" data-testid="filter-type">
          <option value="">All Types</option>
          {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterMode} onChange={e => setFilterMode(e.target.value)} className="flex-1 min-w-0 px-2 sm:px-3 py-2 rounded-xl border text-xs bg-white" data-testid="filter-mode">
          <option value="">All payment modes</option>
          <option value="stripe">Stripe subscription</option>
          <option value="sepa">SEPA mandate</option>
          <option value="na">No subscription (N/A)</option>
        </select>
        <select value={filterDuration} onChange={e => setFilterDuration(e.target.value)} className="flex-1 min-w-0 px-2 sm:px-3 py-2 rounded-xl border text-xs bg-white" data-testid="filter-duration">
          <option value="">Any Duration</option>
          {[1,2,3,4,5,6,7,8,9,10].map(y => <option key={y} value={y}>{y}+ years</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm"><UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">{search ? "No members match your search" : "No members yet"}</p></div>
      ) : (
        <div className="space-y-2">
          {(() => {
            // Group filtered members by family_id, preserving the order the API
            // returned. Only render as a "family card" when >1 member shares a
            // family_id — solo family_ids render flat exactly as before.
            const groups = [];
            const idxByFamily = new Map();
            for (const m of filtered) {
              const fid = m.family_id || `_solo_${m.member_id}`;
              if (idxByFamily.has(fid)) {
                groups[idxByFamily.get(fid)].members.push(m);
              } else {
                idxByFamily.set(fid, groups.length);
                groups.push({ family_id: fid, members: [m] });
              }
            }

            const renderMemberRow = (m) => {
              const isDue = isSubscriptionDue(m);
              return (
                <div key={m.member_id} className="w-full bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`member-${m.member_id}`}>
                  <button onClick={async () => {
                    try { const res = await authAxios.get(`/members/${m.member_id}`); setSelectedMember(res.data); }
                    catch { setSelectedMember(m); }
                  }} className="w-full text-left">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-[#8b5cf6] flex items-center justify-center shrink-0"><span className="text-white font-bold">{m.name?.charAt(0).toUpperCase()}</span></div>
                        <div className="min-w-0">
                          <p className="font-bold text-[#1e3a5f] truncate">{m.name}
                            {m.is_primary_in_family && <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded uppercase tracking-wider">Primary</span>}
                          </p>
                          <p className="text-slate-500 text-sm truncate">{m.email || "\u2014"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                        <div className="text-right">
                          <p className="text-xs text-purple-500 font-medium">{m.member_type_name}</p>
                          <p className="text-slate-400 font-mono text-xs">{m.member_id}</p>
                          {m.subscription_mode === "stripe" ? (
                            <p className="text-xs text-blue-600 font-medium">Stripe subscription</p>
                          ) : m.subscription_mode === "sepa" ? (
                            <p className="text-xs text-emerald-600 font-medium">SEPA mandate</p>
                          ) : isDue ? (
                            <p className="text-xs text-orange-500 font-medium">Subscription due</p>
                          ) : m.last_membership_fee_paid_date ? (
                            <p className="text-xs text-green-500">Fee: {m.last_membership_fee_paid_date}</p>
                          ) : (
                            <p className="text-xs text-amber-500">Fee: unpaid</p>
                          )}
                        </div>
                        <span className={`w-2 h-2 rounded-full ${isDue ? "bg-orange-500" : m.is_active && m.approval_status === "approved" ? "bg-green-500" : m.approval_status === "pending" ? "bg-amber-500" : "bg-red-500"}`}></span>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </button>
                  {isDue && (
                    <div className="mt-3 pt-3 border-t border-orange-100 flex items-center gap-2">
                      <p className="text-xs text-orange-700 flex-1">Awaiting signed SEPA mandate to move this member to &quot;Active Subscriptions&quot;.</p>
                      <label className="cursor-pointer px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center gap-1.5" data-testid={`upload-sepa-${m.member_id}`}>
                        <FileUp className="w-3.5 h-3.5" /> Upload SEPA
                        <input type="file" accept="application/pdf" className="hidden"
                          onChange={e => { if (e.target.files?.[0]) openSepaUploadDialog({ memberId: m.member_id, currentFeeDate: m.last_membership_fee_paid_date }, e.target.files[0]); e.target.value = ""; }}
                        />
                      </label>
                    </div>
                  )}
                </div>
              );
            };

            return groups.map(g => {
              if (g.members.length === 1) return renderMemberRow(g.members[0]);
              // Family card
              const primary = g.members.find(x => x.is_primary_in_family) || g.members[0];
              const dueCount = g.members.filter(isSubscriptionDue).length;
              const expanded = expandedFamilies[g.family_id] !== false; // default open
              return (
                <div key={g.family_id} className="rounded-2xl border-2 border-purple-200 bg-purple-50/40 overflow-hidden" data-testid={`family-group-${g.family_id}`}>
                  <div className="flex items-center gap-3 px-4 py-3 bg-purple-100/60 cursor-pointer" onClick={() => setExpandedFamilies(prev => ({ ...prev, [g.family_id]: !expanded }))}>
                    <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-sm">
                      {primary.name?.charAt(0).toUpperCase() || "F"}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-[#1e3a5f] text-sm">Family: {primary.name} <span className="text-xs text-slate-500 font-normal">({g.members.length} members)</span></p>
                      <p className="text-xs text-purple-700">
                        {dueCount > 0 ? `${dueCount} subscription due` : "All members active"}
                      </p>
                    </div>
                    {dueCount > 0 && (
                      <label className="cursor-pointer px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center gap-1.5 whitespace-nowrap" data-testid={`upload-family-sepa-${g.family_id}`} onClick={e => e.stopPropagation()}>
                        <FileUp className="w-3.5 h-3.5" /> Upload Family SEPA
                        <input type="file" accept="application/pdf" className="hidden"
                          onChange={e => { if (e.target.files?.[0]) openSepaUploadDialog({ familyId: g.family_id, currentFeeDate: primary.last_membership_fee_paid_date }, e.target.files[0]); e.target.value = ""; }}
                        />
                      </label>
                    )}
                    <button className="text-slate-500 text-xs">{expanded ? "\u25B2" : "\u25BC"}</button>
                  </div>
                  {expanded && (
                    <div className="p-3 space-y-2 border-t border-purple-100 bg-white">
                      {g.members.map(renderMemberRow)}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}

      {showAdd && <Modal title="Add Member" onClose={() => setShowAdd(false)}>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" className="w-full p-3 rounded-xl border" data-testid="member-name" />
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="w-full p-3 rounded-xl border" data-testid="member-email" />
          <div><label className="text-xs text-slate-500">Date of Birth *</label><input type="date" max={new Date().toISOString().slice(0,10)} value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} className="w-full p-3 rounded-xl border" data-testid="member-dob" /></div>
          <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address *" className="w-full p-3 rounded-xl border" data-testid="member-address" />
          <input value={form.mobile_number} onChange={e => setForm({ ...form, mobile_number: e.target.value })} placeholder="Mobile Number (optional)" className="w-full p-3 rounded-xl border" data-testid="member-mobile" />
          <div>
            <label className="text-xs text-slate-500">Member Type *</label>
            <select value={form.member_type_id} onChange={e => setForm({ ...form, member_type_id: e.target.value })} className="w-full p-3 rounded-xl border" data-testid="member-type-select">
              <option value="">Select type...</option>
              {types.map(t => <option key={t.id} value={t.id}>{t.name} ({"\u20ac"}{t.annual_fee}/yr)</option>)}
            </select>
          </div>
        </div>
                <button
          onClick={() => toast.info("Adding new members is temporarily disabled")}
          disabled
          className="w-full py-3 rounded-full bg-slate-300 text-slate-500 font-bold mt-4 cursor-not-allowed opacity-60"
          title="Adding new members is temporarily disabled"
          data-testid="save-member"
        >Add Member \u00b7 disabled</button>
      </Modal>}

      {showUpload && <Modal title="Bulk Upload Members" onClose={() => { setShowUpload(false); setBulkResult(null); setBulkPreview(null); setBulkSelected({}); }}>
        <div className="space-y-4">
          {/* Step 1 hidden once preview loaded so the FA focuses on the review table */}
          {!bulkPreview && !bulkResult && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                <p className="text-blue-900 font-medium text-sm mb-1">Step 1 — Download the template</p>
                <p className="text-blue-700 text-xs mb-3">The Instructions sheet lists every column, which are mandatory, and the accepted formats.</p>
                <button onClick={downloadTemplate} className="w-full py-2.5 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center gap-2" data-testid="download-template-btn">
                  <Download className="w-4 h-4" /> Download Excel Template
                </button>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                <p className="text-slate-800 font-medium text-sm mb-1">Step 2 — Upload for preview</p>
                <p className="text-slate-500 text-xs mb-3">
                  You&apos;ll see every row and any errors before anything is written. Only the rows you tick will be activated.
                </p>
                <input type="file" accept=".xlsx" disabled={bulkBusy}
                  onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); e.target.value = ""; }}
                  className="w-full p-3 rounded-xl border bg-white text-sm" data-testid="excel-file-input" />
                {bulkBusy && <p className="text-xs text-slate-500 mt-2">Parsing…</p>}
              </div>
            </>
          )}

          {/* Preview + confirm */}
          {bulkPreview && (
            <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="bulk-preview">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-[#1e3a5f]">Preview: {bulkPreview.filename}</p>
                <button onClick={cancelBulkPreview} className="text-xs text-slate-500 hover:underline">Cancel</button>
              </div>
              <div className="flex gap-2 mb-3 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">{bulkPreview.valid_count} valid</span>
                <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">{bulkPreview.error_count} errors</span>
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{bulkPreview.total} total</span>
              </div>
              <div className="flex items-center gap-2 mb-2 text-xs">
                <button
                  onClick={() => {
                    const all = {};
                    bulkPreview.rows.forEach(r => { all[r.row_num] = !r.error; });
                    setBulkSelected(all);
                  }}
                  className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600"
                  data-testid="bulk-select-all"
                >Select all valid</button>
                <button
                  onClick={() => setBulkSelected({})}
                  className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600"
                  data-testid="bulk-select-none"
                >Deselect all</button>
              </div>
              <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1" data-testid="bulk-preview-rows">
                {bulkPreview.rows.map(r => (
                  <label
                    key={`row-${r.row_num}`}
                    className={`flex items-start gap-2 p-2 rounded-lg text-xs cursor-pointer ${r.error ? "bg-red-50 border border-red-200" : "bg-slate-50 hover:bg-slate-100"}`}
                    data-testid={`bulk-row-${r.row_num}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!bulkSelected[r.row_num]}
                      disabled={!!r.error}
                      onChange={e => setBulkSelected(prev => ({ ...prev, [r.row_num]: e.target.checked }))}
                      className="mt-1"
                      data-testid={`bulk-row-checkbox-${r.row_num}`}
                    />
                    <div className="flex-1">
                      <p className={`font-medium ${r.error ? "text-red-700" : "text-[#1e3a5f]"}`}>
                        Row {r.row_num}: {r.full_name || <span className="italic text-slate-400">(no name)</span>}
                      </p>
                      <p className="text-slate-500">
                        {r.email || "\u2014"} · {r.member_type || "\u2014"} · DOB {r.dob || "\u2014"}
                      </p>
                      {r.error && <p className="text-red-600 mt-1">{r.error}</p>}
                    </div>
                  </label>
                ))}
              </div>
              <button
                onClick={confirmBulkCommit}
                disabled={bulkBusy || Object.values(bulkSelected).filter(Boolean).length === 0}
                className="mt-3 w-full py-2.5 rounded-full bg-[#1e3a5f] text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="bulk-commit-btn"
              >
                {bulkBusy ? "Importing…" : `Import ${Object.values(bulkSelected).filter(Boolean).length} row(s)`}
              </button>
            </div>
          )}

          {/* Result summary */}
          {bulkResult && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <p className="text-sm font-bold text-[#1e3a5f] mb-2">Import Result</p>
              <p className="text-sm text-green-600 mb-2" data-testid="bulk-created-count">
                <Check className="w-4 h-4 inline mr-1" /> {bulkResult.created_count} member(s) activated
              </p>
              {bulkResult.errors?.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1" data-testid="bulk-errors">
                  <p className="text-sm text-red-600 font-medium">{bulkResult.errors.length} row(s) skipped:</p>
                  {bulkResult.errors.map((err, i) => (
                    <p key={`err-${err.row}-${i}`} className="text-xs text-red-600 pl-4">Row {err.row}: {err.error}</p>
                  ))}
                </div>
              )}
              <button onClick={() => { setShowUpload(false); setBulkResult(null); setBulkPreview(null); setBulkSelected({}); setFilterStatus("subscription_due"); }}
                className="mt-3 w-full py-2.5 rounded-full bg-[#1e3a5f] text-white font-bold text-sm"
                data-testid="close-bulk-result"
              >
                View Subscription Due members
              </button>
            </div>
          )}
        </div>
      </Modal>}

      {/* iter128: FA deactivate confirmation dialog — preview Stripe sub +
          annual fee + child cascade so accidental clicks are near-zero.
          iter132: dialog is declared as a variable and rendered in both
          the list and detail return trees so it opens immediately on the
          member profile, not only after clicking Back. */}
      {sepaUploadModal}
      {deactivateDialog}

    </div>
  );
}
